import { formatFilamentGrams, formatProductionDurationMinutes } from './jobs';

const FILAMENT_TYPE_PATTERN = /(?:^|[-_.])(PLA\+?|PETG|ABS|TPU|ASA|PA|PC|NYLON|HIPS|PVA|PLA-CF|PETG-CF|SILK-PLA)(?:[-_.]|$)/i;

/** Cores literais (PT) → hex aproximado. Ordem: nomes compostos primeiro. */
const NAMED_COLOR_ENTRIES = [
    ['verde claro', '#86efac'],
    ['verde escuro', '#15803d'],
    ['azul claro', '#60a5fa'],
    ['azul escuro', '#1d4ed8'],
    ['vermelho escuro', '#b91c1c'],
    ['rosa claro', '#f9a8d4'],
    ['cinza claro', '#d1d5db'],
    ['cinza escuro', '#4b5563'],
    ['amarelo claro', '#fde047'],
    ['laranja claro', '#fdba74'],
    ['roxo claro', '#c4b5fd'],
    ['roxo escuro', '#6d28d9'],
    ['marrom claro', '#a16207'],
    ['dourado', '#ca8a04'],
    ['prata', '#c0c0c0'],
    ['bronze', '#cd7f32'],
    ['bege', '#d4a574'],
    ['natural', '#d4a574'],
    ['transparente', 'transparent'],
    ['translucido', 'transparent'],
    ['translúcido', 'transparent'],
    ['branco', '#f5f5f5'],
    ['preto', '#1a1a1a'],
    ['azul', '#2563eb'],
    ['verde', '#22c55e'],
    ['vermelho', '#ef4444'],
    ['amarelo', '#eab308'],
    ['laranja', '#f97316'],
    ['rosa', '#ec4899'],
    ['roxo', '#a855f7'],
    ['violeta', '#8b5cf6'],
    ['cinza', '#9ca3af'],
    ['marrom', '#78350f'],
    ['ciano', '#06b6d4'],
    ['turquesa', '#14b8a6'],
    ['lilas', '#c084fc'],
    ['lilás', '#c084fc'],
    ['creme', '#fef3c7'],
    ['off white', '#fafafa'],
];

function normalizeColorKey(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

function isHexColor(value) {
    const trimmed = String(value || '').trim();
    return /^#?[0-9a-fA-F]{3}([0-9a-fA-F]{3})?([0-9a-fA-F]{2})?$/.test(trimmed)
        || /^0x[0-9a-fA-F]{6}$/i.test(trimmed);
}

function expandHex(value) {
    let hex = String(value || '').trim();
    if (hex.startsWith('0x') || hex.startsWith('0X')) {
        hex = `#${hex.slice(2)}`;
    }
    if (!hex.startsWith('#')) {
        hex = `#${hex}`;
    }
    if (hex.length === 4) {
        const r = hex[1];
        const g = hex[2];
        const b = hex[3];
        return `#${r}${r}${g}${g}${b}${b}`;
    }
    return hex.slice(0, 7);
}

function namedColorToCss(name) {
    const key = normalizeColorKey(name);
    if (!key) {
        return null;
    }
    for (const [label, css] of NAMED_COLOR_ENTRIES) {
        if (key === normalizeColorKey(label) || key.includes(normalizeColorKey(label))) {
            return { css, label: name.trim() };
        }
    }
    return null;
}

function colorNeedsBorder(css) {
    if (css === 'transparent') {
        return true;
    }
    const hex = expandHex(css);
    if (!hex.startsWith('#') || hex.length < 7) {
        return false;
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.82;
}

/** Converte cor (hex ou nome) para preview CSS. */
export function resolveJobColorPreview(rawColor) {
    const label = String(rawColor || '').trim();
    if (!label) {
        return null;
    }
    if (isHexColor(label)) {
        const css = expandHex(label);
        return { label, css, needsBorder: colorNeedsBorder(css) };
    }
    const named = namedColorToCss(label);
    if (named) {
        return {
            label,
            css: named.css,
            needsBorder: colorNeedsBorder(named.css),
        };
    }
    return null;
}

function splitColorTokens(value) {
    return String(value || '')
        .split(/[/|,;+]+/)
        .map((part) => part.trim())
        .filter(Boolean);
}

export function extractJobColorPreviews(job) {
    const seen = new Set();
    const previews = [];

    const pushColor = (raw) => {
        const preview = resolveJobColorPreview(raw);
        if (!preview) {
            return;
        }
        const key = `${preview.css}|${preview.label.toLowerCase()}`;
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        previews.push(preview);
    };

    (job?.filaments || []).forEach((filament) => {
        splitColorTokens(filament?.color).forEach(pushColor);
    });

    const metadata = job?.metadata || {};
    const metaColors = metadata['filament.colors']
        ?? metadata.filament_colors
        ?? metadata.extruder_colors;
    if (Array.isArray(metaColors)) {
        metaColors.forEach((color) => pushColor(color));
    } else if (typeof metaColors === 'string') {
        splitColorTokens(metaColors).forEach(pushColor);
    }

    return previews;
}

export function parseFilamentTypeToken(value) {
    const token = String(value || '').trim();
    if (!token) {
        return null;
    }
    const match = token.match(FILAMENT_TYPE_PATTERN);
    if (match) {
        return match[1].toUpperCase();
    }
    const upper = token.toUpperCase();
    if (/^(PLA\+?|PETG|ABS|TPU|ASA|PA|PC|NYLON|HIPS|PVA|PLA-CF|PETG-CF|SILK-PLA)$/.test(upper)) {
        return upper;
    }
    return null;
}

export function parseFilamentTypeFromFilename(fileName) {
    const match = String(fileName || '').match(FILAMENT_TYPE_PATTERN);
    return match ? match[1].toUpperCase() : null;
}

function extractFilamentTypeFromMetadata(metadata) {
    if (!metadata || typeof metadata !== 'object') {
        return null;
    }
    const filament = metadata.filament && typeof metadata.filament === 'object'
        ? metadata.filament
        : null;

    const candidates = [
        metadata.filament_type,
        metadata['filament.type'],
        filament?.type,
        filament?.name,
    ];

    for (const candidate of candidates) {
        const parsed = parseFilamentTypeToken(candidate);
        if (parsed) {
            return parsed;
        }
    }
    return null;
}

export function extractJobFilamentType(job) {
    for (const filament of job?.filaments || []) {
        const fromFilament = parseFilamentTypeToken(filament?.type);
        if (fromFilament) {
            return fromFilament;
        }
    }

    const fromMetadata = extractFilamentTypeFromMetadata(job?.metadata);
    if (fromMetadata) {
        return fromMetadata;
    }

    return parseFilamentTypeFromFilename(job?.file_name);
}

/** Gramas estimadas persistidas no job (fonte da verdade). */
export function resolveJobFilamentGrams(job) {
    return Math.max(0, Math.round(Number(job?.estimated_filament_consumption_grams) || 0));
}

export function getJobDisplaySpecs(job) {
    const durationMinutes = Math.max(0, Math.round(Number(job?.estimated_duration_minutes) || 0));
    const filamentGrams = resolveJobFilamentGrams(job);

    const colors = extractJobColorPreviews(job);
    const filamentType = extractJobFilamentType(job);

    return {
        durationMinutes,
        durationLabel: durationMinutes > 0 ? formatProductionDurationMinutes(durationMinutes) : null,
        filamentGrams,
        filamentLabel: filamentGrams > 0 ? formatFilamentGrams(filamentGrams) : null,
        filamentType,
        colors,
        hasSpecs: durationMinutes > 0 || filamentGrams > 0 || Boolean(filamentType) || colors.length > 0,
    };
}
